"use client";

import type {
	DragEndEvent,
	DragOverEvent,
	DragStartEvent,
	DropAnimation,
	Modifiers,
	UniqueIdentifier,
} from "@dnd-kit/core";
import {
	DndContext,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
	DragOverlay,
	defaultDropAnimationSideEffects,
	MeasuringStrategy,
} from "@dnd-kit/core";
import type { AnimateLayoutChanges } from "@dnd-kit/sortable";
import {
	arrayMove,
	defaultAnimateLayoutChanges,
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Slot } from "@radix-ui/react-slot";
import type * as React from "react";
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../../../lib";
import { triggerSqDndDragStartHaptic } from "../../../lib/dnd/sq-dnd-haptic";
import { useSqDndSensors } from "../../../lib/dnd/sq-dnd-sensors";

interface KanbanContextProps {
	columns: Record<string, unknown[]>;
	setColumns: (columns: Record<string, unknown[]>) => void;
	getItemId: (item: unknown) => string;
	columnIds: string[];
	activeId: UniqueIdentifier | null;
	setActiveId: (id: UniqueIdentifier | null) => void;
	findContainer: (id: UniqueIdentifier) => string | undefined;
	isColumn: (id: UniqueIdentifier) => boolean;
	modifiers?: Modifiers;
}

const KanbanContext = createContext<KanbanContextProps>({
	columns: {},
	setColumns: () => {},
	getItemId: () => "",
	columnIds: [],
	activeId: null,
	setActiveId: () => {},
	findContainer: () => undefined,
	isColumn: () => false,
	modifiers: undefined,
});

const ColumnContext = createContext<{
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners | undefined;
	isDragging?: boolean;
	disabled?: boolean;
}>({
	attributes: {} as DraggableAttributes,
	listeners: undefined,
	isDragging: false,
	disabled: false,
});

const ItemContext = createContext<{
	listeners: DraggableSyntheticListeners | undefined;
	isDragging?: boolean;
	disabled?: boolean;
}>({
	listeners: undefined,
	isDragging: false,
	disabled: false,
});

const IsOverlayContext = createContext(false);

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
	defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const dropAnimationConfig: DropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: {
			active: {
				opacity: "0.4",
			},
		},
	}),
};

export interface KanbanMoveEvent {
	event: DragEndEvent;
	activeContainer: string;
	activeIndex: number;
	overContainer: string;
	overIndex: number;
}

export interface KanbanRootProps<T> extends HTMLAttributes<HTMLDivElement> {
	value: Record<string, T[]>;
	onValueChange: (value: Record<string, T[]>) => void;
	getItemValue: (item: T) => string;
	children: ReactNode;
	onMove?: (event: KanbanMoveEvent) => void;
	asChild?: boolean;
	modifiers?: Modifiers;
}

export function Kanban<T>({
	value,
	onValueChange,
	getItemValue,
	children,
	className,
	asChild = false,
	onMove,
	modifiers,
	...props
}: KanbanRootProps<T>) {
	const columns = value;
	const setColumns = onValueChange;
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

	const sensors = useSqDndSensors({
		distance: 10,
		touchDelay: 250,
		touchTolerance: 5,
		keyboardCoordinateGetter: sortableKeyboardCoordinates,
	});

	const columnIds = useMemo(() => Object.keys(columns), [columns]);

	const isColumn = useCallback(
		(id: UniqueIdentifier) => columnIds.includes(id as string),
		[columnIds],
	);

	const findContainer = useCallback(
		(id: UniqueIdentifier) => {
			if (isColumn(id)) {
				return id as string;
			}
			return columnIds.find((key) =>
				columns[key].some((item) => getItemValue(item) === id),
			);
		},
		[columns, columnIds, getItemValue, isColumn],
	);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		triggerSqDndDragStartHaptic();
		setActiveId(event.active.id);
	}, []);

	const handleDragOver = useCallback(
		(event: DragOverEvent) => {
			if (onMove) {
				return;
			}

			const { active, over } = event;
			if (!over) {
				return;
			}

			if (isColumn(active.id)) {
				return;
			}

			const activeContainer = findContainer(active.id);
			const overContainer = findContainer(over.id);

			if (!activeContainer || !overContainer) {
				return;
			}

			if (activeContainer !== overContainer) {
				const activeItems = columns[activeContainer];
				const overItems = columns[overContainer];

				const activeIndex = activeItems.findIndex(
					(item: T) => getItemValue(item) === active.id,
				);
				let overIndex = overItems.findIndex(
					(item: T) => getItemValue(item) === over.id,
				);

				// If dropping on the column itself, not an item
				if (isColumn(over.id)) {
					overIndex = overItems.length;
				}

				const newActiveItems = [...activeItems];
				const newOverItems = [...overItems];
				const [movedItem] = newActiveItems.splice(activeIndex, 1);
				newOverItems.splice(overIndex, 0, movedItem);

				setColumns({
					...columns,
					[activeContainer]: newActiveItems,
					[overContainer]: newOverItems,
				});
			} else {
				const container = activeContainer;
				const activeIndex = columns[container].findIndex(
					(item: T) => getItemValue(item) === active.id,
				);
				const overIndex = columns[container].findIndex(
					(item: T) => getItemValue(item) === over.id,
				);

				if (activeIndex !== overIndex) {
					setColumns({
						...columns,
						[container]: arrayMove(
							columns[container],
							activeIndex,
							overIndex,
						),
					});
				}
			}
		},
		[findContainer, getItemValue, isColumn, setColumns, columns, onMove],
	);

	const handleDragCancel = useCallback(() => {
		setActiveId(null);
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			setActiveId(null);

			if (!over) {
				return;
			}

			// Handle item move callback
			if (onMove && !isColumn(active.id)) {
				const activeContainer = findContainer(active.id);
				const overContainer = findContainer(over.id);

				if (activeContainer && overContainer) {
					const activeIndex = columns[activeContainer].findIndex(
						(item: T) => getItemValue(item) === active.id,
					);
					const overIndex = isColumn(over.id)
						? columns[overContainer].length
						: columns[overContainer].findIndex(
								(item: T) => getItemValue(item) === over.id,
							);

					onMove({
						event,
						activeContainer,
						activeIndex,
						overContainer,
						overIndex,
					});
				}
				return;
			}

			// Handle column reordering
			if (isColumn(active.id) && isColumn(over.id)) {
				const activeIndex = columnIds.indexOf(active.id as string);
				const overIndex = columnIds.indexOf(over.id as string);
				if (activeIndex !== overIndex) {
					const newOrder = arrayMove(
						Object.keys(columns),
						activeIndex,
						overIndex,
					);
					const newColumns: Record<string, T[]> = {};
					for (const key of newOrder) {
						newColumns[key] = columns[key];
					}
					setColumns(newColumns);
				}
				return;
			}

			const activeContainer = findContainer(active.id);
			const overContainer = findContainer(over.id);

			// Handle item reordering within the same column
			if (
				activeContainer &&
				overContainer &&
				activeContainer === overContainer
			) {
				const container = activeContainer;
				const activeIndex = columns[container].findIndex(
					(item: T) => getItemValue(item) === active.id,
				);
				const overIndex = columns[container].findIndex(
					(item: T) => getItemValue(item) === over.id,
				);

				if (activeIndex !== overIndex) {
					setColumns({
						...columns,
						[container]: arrayMove(
							columns[container],
							activeIndex,
							overIndex,
						),
					});
				}
			}
		},
		[
			columnIds,
			columns,
			findContainer,
			getItemValue,
			isColumn,
			setColumns,
			onMove,
		],
	);

	const contextValue = useMemo(
		() => ({
			columns: columns as Record<string, unknown[]>,
			setColumns: (next: Record<string, unknown[]>) => {
				setColumns(next as Record<string, T[]>);
			},
			getItemId: (item: unknown) => getItemValue(item as T),
			columnIds,
			activeId,
			setActiveId,
			findContainer,
			isColumn,
			modifiers,
		}),
		[
			columns,
			setColumns,
			getItemValue,
			columnIds,
			activeId,
			findContainer,
			isColumn,
			modifiers,
		],
	);

	const Comp = asChild ? Slot : "div";

	return (
		<KanbanContext.Provider value={contextValue}>
			<DndContext
				sensors={sensors}
				modifiers={modifiers}
				measuring={{
					droppable: {
						strategy: MeasuringStrategy.Always,
					},
				}}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<Comp
					data-slot="kanban"
					data-dragging={activeId !== null}
					className={cn(
						activeId !== null && "cursor-grabbing!",
						className,
					)}
					{...props}
				>
					{children}
				</Comp>
			</DndContext>
		</KanbanContext.Provider>
	);
}

export interface KanbanBoardProps extends HTMLAttributes<HTMLDivElement> {
	asChild?: boolean;
}

export function KanbanBoard({
	className,
	asChild = false,
	children,
	...props
}: KanbanBoardProps) {
	const { columnIds } = useContext(KanbanContext);
	const Comp = asChild ? Slot : "div";

	return (
		<SortableContext items={columnIds} strategy={rectSortingStrategy}>
			<Comp
				data-slot="kanban-board"
				className={cn(
					"grid auto-rows-fr gap-4 sm:grid-cols-3",
					className,
				)}
				{...props}
			>
				{children}
			</Comp>
		</SortableContext>
	);
}

export interface KanbanColumnProps extends HTMLAttributes<HTMLDivElement> {
	value: string;
	disabled?: boolean;
	asChild?: boolean;
}

export function KanbanColumn({
	value,
	className,
	asChild = false,
	disabled,
	children,
	...props
}: KanbanColumnProps) {
	const isOverlay = useContext(IsOverlayContext);

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isOverlay,
		animateLayoutChanges,
	});

	const { activeId, isColumn } = useContext(KanbanContext);
	const isColumnDragging = activeId ? isColumn(activeId) : false;

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	} satisfies CSSProperties;

	const Comp = asChild ? Slot : "div";

	if (isOverlay) {
		return (
			<ColumnContext.Provider
				value={{
					attributes: {} as DraggableAttributes,
					listeners: undefined,
					isDragging: true,
					disabled: false,
				}}
			>
				<Comp
					data-slot="kanban-column"
					data-value={value}
					data-dragging
					className={cn(
						"group/kanban-column flex flex-col",
						className,
					)}
					{...props}
				>
					{children}
				</Comp>
			</ColumnContext.Provider>
		);
	}

	return (
		<ColumnContext.Provider
			value={{
				attributes,
				listeners,
				isDragging: isColumnDragging,
				disabled,
			}}
		>
			<Comp
				data-slot="kanban-column"
				data-value={value}
				data-dragging={isSortableDragging}
				data-disabled={disabled}
				ref={setNodeRef}
				style={style}
				className={cn(
					"group/kanban-column flex flex-col",
					isSortableDragging && "z-50 opacity-50",
					disabled && "opacity-50",
					className,
				)}
				{...props}
			>
				{children}
			</Comp>
		</ColumnContext.Provider>
	);
}

export interface KanbanColumnHandleProps
	extends HTMLAttributes<HTMLDivElement> {
	cursor?: boolean;
	asChild?: boolean;
}

export function KanbanColumnHandle({
	className,
	asChild = false,
	cursor = true,
	children,
	...props
}: KanbanColumnHandleProps) {
	const { attributes, listeners, isDragging, disabled } =
		useContext(ColumnContext);

	const Comp = asChild ? Slot : "div";

	return (
		<Comp
			data-slot="kanban-column-handle"
			data-dragging={isDragging}
			data-disabled={disabled}
			{...attributes}
			{...listeners}
			className={cn(
				"opacity-0 transition-opacity group-hover/kanban-column:opacity-100",
				cursor && (isDragging ? "cursor-grabbing!" : "cursor-grab!"),
				className,
			)}
			{...props}
		>
			{children}
		</Comp>
	);
}

export interface KanbanItemProps extends HTMLAttributes<HTMLDivElement> {
	value: string;
	disabled?: boolean;
	asChild?: boolean;
}

export function KanbanItem({
	value,
	className,
	asChild = false,
	disabled,
	children,
	...props
}: KanbanItemProps) {
	const isOverlay = useContext(IsOverlayContext);

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isOverlay,
		animateLayoutChanges,
	});

	const { activeId, isColumn } = useContext(KanbanContext);
	const isItemDragging = activeId ? !isColumn(activeId) : false;

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	} satisfies CSSProperties;

	const Comp = asChild ? Slot : "div";

	if (isOverlay) {
		return (
			<ItemContext.Provider
				value={{
					listeners: undefined,
					isDragging: true,
					disabled: false,
				}}
			>
				<Comp
					data-slot="kanban-item"
					data-value={value}
					data-dragging
					className={cn(className)}
					{...props}
				>
					{children}
				</Comp>
			</ItemContext.Provider>
		);
	}

	return (
		<ItemContext.Provider
			value={{ listeners, isDragging: isItemDragging, disabled }}
		>
			<Comp
				data-slot="kanban-item"
				data-value={value}
				data-dragging={isSortableDragging}
				data-disabled={disabled}
				ref={setNodeRef}
				style={style}
				{...attributes}
				className={cn(
					isSortableDragging && "z-50 opacity-50",
					disabled && "opacity-50",
					className,
				)}
				{...props}
			>
				{children}
			</Comp>
		</ItemContext.Provider>
	);
}

export interface KanbanItemHandleProps extends HTMLAttributes<HTMLDivElement> {
	cursor?: boolean;
	asChild?: boolean;
}

export function KanbanItemHandle({
	className,
	asChild = false,
	cursor = true,
	children,
	...props
}: KanbanItemHandleProps) {
	const { listeners, isDragging, disabled } = useContext(ItemContext);

	const Comp = asChild ? Slot : "div";

	return (
		<Comp
			data-slot="kanban-item-handle"
			data-dragging={isDragging}
			data-disabled={disabled}
			{...listeners}
			className={cn(
				cursor && (isDragging ? "cursor-grabbing!" : "cursor-grab!"),
				className,
			)}
			{...props}
		>
			{children}
		</Comp>
	);
}

export interface KanbanColumnContentProps
	extends HTMLAttributes<HTMLDivElement> {
	value: string;
	asChild?: boolean;
}

export function KanbanColumnContent({
	value,
	className,
	asChild = false,
	children,
	...props
}: KanbanColumnContentProps) {
	const { columns, getItemId } = useContext(KanbanContext);

	const itemIds = useMemo(
		() => columns[value].map(getItemId),
		[columns, getItemId, value],
	);

	const Comp = asChild ? Slot : "div";

	return (
		<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
			<Comp
				data-slot="kanban-column-content"
				className={cn("flex flex-col gap-2", className)}
				{...props}
			>
				{children}
			</Comp>
		</SortableContext>
	);
}

export interface KanbanOverlayProps
	extends Omit<React.ComponentProps<typeof DragOverlay>, "children"> {
	children?:
		| ReactNode
		| ((params: {
				value: UniqueIdentifier;
				variant: "column" | "item";
		  }) => ReactNode);
}

export function KanbanOverlay({
	children,
	className,
	...props
}: KanbanOverlayProps) {
	const { activeId, isColumn, modifiers } = useContext(KanbanContext);
	const [mounted, setMounted] = useState(false);

	useLayoutEffect(() => setMounted(true), []);

	const variant = activeId
		? isColumn(activeId)
			? "column"
			: "item"
		: "item";

	const content =
		activeId && children
			? typeof children === "function"
				? children({ value: activeId, variant })
				: children
			: null;

	if (!mounted) {
		return null;
	}

	return createPortal(
		<DragOverlay
			dropAnimation={dropAnimationConfig}
			modifiers={modifiers}
			className={cn("z-50", activeId && "cursor-grabbing", className)}
			{...props}
		>
			<IsOverlayContext.Provider value>
				{content}
			</IsOverlayContext.Provider>
		</DragOverlay>,
		document.body,
	);
}
